"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";
import CheckIcon from "@/icons/__generated__/Check/Check20";
import ChevronDownIcon from "@/icons/__generated__/ChevronDown/ChevronDown20";
import ChevronUpIcon from "@/icons/__generated__/ChevronUp/ChevronUp20";
import { cn } from "@/lib/utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = ({
  placeholder,
  disabled,
  children,
}: SelectPrimitive.SelectValueProps & {
  disabled?: boolean;
}) => {
  return (
    <SelectPrimitive.Value
      placeholder={
        placeholder && (
          <span
            className={cn("tw-text-gray-600", {
              "tw-text-gray-500": disabled,
            })}
          >
            {placeholder}
          </span>
        )
      }
    >
      {children}
    </SelectPrimitive.Value>
  );
};

const selectTriggerVariants = cva(
  "tw-relative tw-px-12 tw-py-8 tw-inline-flex tw-items-center tw-gap-12 tw-bg-base-white focus:tw-outline focus:tw-outline-2 focus:tw-outline-blue-800 focus:tw-outline-offset-1 tw-border-gray-400 focus:tw-border-gray-900 tw-border tw-rounded-4 tw-text-gray-900 placeholder:tw-text-gray-600 disabled:tw-text-gray-500 disabled:placeholder:tw-text-gray-500 tw-text-nowrap [&_.tw-select-trigger-children]:tw-truncate",
  {
    variants: {
      disabled: {
        true: "tw-text-gray-500",
        false: "",
      },
      fullWidth: {
        true: "tw-w-full",
        false: "",
      },
      error: {
        true: "tw-border-red-800 focus:tw-border-red-800",
        false: "",
      },
      readOnly: {
        true: "tw-pointer-events-none",
        false: "",
      },
      prefix: {
        true: "",
        false: "",
      },
      suffix: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        prefix: true,
        class: "tw-pl-40 tw-pr-12",
      },
      {
        suffix: true,
        class: "tw-pr-40 tw-pl-12",
      },
      {
        suffix: true,
        prefix: true,
        class: "tw-pl-40 tw-pr-40",
      },
    ],
  },
);

export type SelectTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  "prefix"
> &
  Omit<
    VariantProps<typeof selectTriggerVariants>,
    "prefix" | "suffix" | "error"
  > & {
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    fullWidth?: boolean;
    testId?: string;
    errorMsg?: string | undefined;
  };

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    {
      className,
      children,
      prefix,
      suffix,
      errorMsg,
      fullWidth,
      testId,
      ...triggerProps
    },
    ref,
  ) => {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        data-testid={testId}
        translate="no"
        className={cn(
          selectTriggerVariants({
            prefix: !!prefix,
            suffix: true,
            error: !!errorMsg,
            disabled: !!triggerProps.disabled,
            readOnly: !!triggerProps.readOnly,
            fullWidth,
          }),
          className,
        )}
        {...triggerProps}
      >
        {prefix && (
          <SelectPrimitive.Icon asChild>
            <span className="tw-h-full tw-inline-flex tw-absolute tw-top-0 tw-left-12 tw-items-center tw-justify-center tw-w-20">
              {prefix}
            </span>
          </SelectPrimitive.Icon>
        )}
        <span className="tw-select-trigger-children">{children}</span>
        <SelectPrimitive.Icon asChild>
          <span className="tw-h-full tw-inline-flex tw-absolute tw-top-0 tw-right-12 tw-items-center tw-justify-center tw-w-20">
            {suffix || <ChevronDownIcon />}
          </span>
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  },
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "tw-flex tw-cursor-default tw-items-center tw-justify-center tw-py-4",
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "tw-flex tw-cursor-default tw-items-center tw-justify-center tw-py-4",
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "tw-relative tw-z-max tw-overflow-hidden tw-bg-base-white tw-shadow-lg tw-rounded-4 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[side=bottom]:tw-slide-in-from-top-8 data-[side=left]:tw-slide-in-from-right-8 data-[side=right]:tw-slide-in-from-left-8 data-[side=top]:tw-slide-in-from-bottom-8",
        position === "popper" &&
          "data-[side=bottom]:tw-translate-y-4 data-[side=left]:tw--translate-x-4 data-[side=right]:tw-translate-x-4 data-[side=top]:tw--translate-y-4",
        className,
      )}
      style={{ maxHeight: "268px" }}
      position={position}
      translate="no"
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "tw-p-1",
          position === "popper" &&
            "tw-h-[var(--radix-select-trigger-height)] tw-w-full tw-min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("tw-py-4 tw-px-8 tw-body-2-m", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    testId?: string;
  }
>(({ className, testId, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-testid={testId}
    className={cn(
      "tw-relative tw-flex tw-w-full tw-cursor-default tw-select-none tw-items-center tw-justify-between tw-py-8 tw-px-16 tw-body-2 tw-outline-none data-[disabled]:tw-pointer-events-none data-[disabled]:tw-text-gray-500 focus:tw-bg-gray-300",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="tw-flex tw-items-center tw-justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("tw--mx-4 tw-my-4 tw-h-px tw-bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
